/**
 * Authenticated Layout Component
 * Shared layout for all authenticated pages with navigation and hero background
 */

import { ReactNode, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button, Box, Typography } from '@mui/material';
import { useAuth } from '@shared/features/auth';
import { SubNavigationBar } from './SubNavigationBar';
import { InstrumentSidebar } from '../instruments/InstrumentSidebar';
import { Instrument } from '@shared/types/instrument';

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [_selectedInstrument, setSelectedInstrument] = useState<Instrument | null>(null);

  // Note: Watchlist loading is handled by InstrumentSidebar component
  // to avoid duplicate API calls and constant refreshing

  // Check if a route is active
  const isActiveRoute = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleInstrumentSelect = (instrument: Instrument) => {
    setSelectedInstrument(instrument);
    // TODO: Navigate to instrument details or update main content
    // eslint-disable-next-line no-console
    console.log('Selected instrument:', instrument);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fff', color: '#04377b', position: 'relative' }}>
      <Box
        component="header"
        sx={{
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
          border: '1px solid',
          borderColor: 'rgba(4, 55, 123, 0.3)',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          py: 2,
          px: { xs: 3, md: 6 },
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backdropFilter: 'blur(10px)',
          boxShadow: `
            0 0 0 1px rgba(4, 55, 123, 0.15),
            0 0 8px rgba(4, 55, 123, 0.2),
            0 0 15px rgba(4, 55, 123, 0.15),
            0 3px 16px rgba(4, 55, 123, 0.25),
            0 1px 6px rgba(4, 55, 123, 0.15),
            inset 0 0 10px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(4, 55, 123, 0.1)
          `,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          height: '64px', // Explicit height: 16px top padding + 32px content + 16px bottom padding
          minHeight: '64px',
        }}
      >
        {/* Logo */}
        <Box component="img" src="/assets/flowrithm-logo.png" alt="FlowRithm logo" sx={{ height: 32 }} />

        {/* Navigation items - Start at 240px from viewport */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            position: 'relative',
            ml: {
              xs: 0,
              md: 'calc(240px - 48px)' // 240px (desired position) - 48px (header padding md: 6 * 8px)
            },
            flex: 1,
          }}
        >
          <Button
            component={Link}
            to="/ai-assist"
            variant={isActiveRoute('/ai-assist') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/ai-assist') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/ai-assist') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/ai-assist') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            AI Assist
          </Button>
          <Button
            component={Link}
            to="/broker-accounts"
            variant={isActiveRoute('/broker-accounts') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/broker-accounts') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/broker-accounts') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/broker-accounts') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Broker Accounts
          </Button>
          <Button
            component={Link}
            to="/orders"
            variant={isActiveRoute('/orders') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/orders') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/orders') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/orders') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Orders
          </Button>
          <Button
            component={Link}
            to="/portfolio"
            variant={isActiveRoute('/portfolio') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/portfolio') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/portfolio') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/portfolio') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Portfolio
          </Button>
          <Button
            component={Link}
            to="/analytics"
            variant={isActiveRoute('/analytics') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/analytics') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/analytics') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/analytics') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Analytics
          </Button>
          {isAdmin && (
            <Button
              component={Link}
              to="/admin"
              variant={isActiveRoute('/admin') ? 'contained' : 'text'}
              sx={{
                textTransform: 'none',
                color: isActiveRoute('/admin') ? '#fff' : '#8B0000',
                bgcolor: isActiveRoute('/admin') ? '#8B0000' : 'transparent',
                border: isActiveRoute('/admin') ? 'none' : '1px solid rgba(139, 0, 0, 0.3)',
                '&:hover': {
                  bgcolor: isActiveRoute('/admin') ? '#6B0000' : 'rgba(139, 0, 0, 0.05)',
                },
              }}
            >
              Admin
            </Button>
          )}
        </Box>

        {/* BETA Version Message */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 'auto', mr: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#FF6F00',
              fontWeight: 700,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              // Embossed 3D effect with multiple shadows
              textShadow: `
                0 1px 0 rgba(255, 255, 255, 0.9),
                0 2px 2px rgba(0, 0, 0, 0.3),
                0 3px 4px rgba(0, 0, 0, 0.2),
                0 4px 6px rgba(0, 0, 0, 0.15),
                0 0 10px rgba(255, 152, 0, 0.5),
                0 0 15px rgba(255, 152, 0, 0.4),
                0 0 20px rgba(255, 152, 0, 0.3),
                inset 0 1px 1px rgba(255, 255, 255, 0.4),
                inset 0 -1px 1px rgba(0, 0, 0, 0.3)
              `,
              // Flashy pulsing animation
              animation: 'flashyPulse 2s ease-in-out infinite',
              '@keyframes flashyPulse': {
                '0%, 100%': {
                  opacity: 1,
                  filter: 'brightness(1) drop-shadow(0 0 8px rgba(255, 152, 0, 0.6))',
                  transform: 'scale(1)',
                },
                '50%': {
                  opacity: 0.9,
                  filter: 'brightness(1.4) drop-shadow(0 0 15px rgba(255, 152, 0, 0.8))',
                  transform: 'scale(1.02)',
                },
              },
              // Enhanced glow on hover
              '&:hover': {
                filter: 'brightness(1.5) drop-shadow(0 0 20px rgba(255, 152, 0, 0.9))',
                textShadow: `
                  0 1px 0 rgba(255, 255, 255, 1),
                  0 2px 2px rgba(0, 0, 0, 0.35),
                  0 3px 4px rgba(0, 0, 0, 0.25),
                  0 4px 6px rgba(0, 0, 0, 0.2),
                  0 0 15px rgba(255, 152, 0, 0.7),
                  0 0 25px rgba(255, 152, 0, 0.6),
                  0 0 35px rgba(255, 152, 0, 0.5),
                  inset 0 1px 1px rgba(255, 255, 255, 0.5),
                  inset 0 -1px 1px rgba(0, 0, 0, 0.4)
                `,
                transform: 'scale(1.05)',
              },
              // Padding and background for better visibility
              px: 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: '1px solid rgba(255, 152, 0, 0.4)',
              backgroundColor: 'rgba(255, 152, 0, 0.12)',
              boxShadow: `
                inset 0 1px 2px rgba(255, 255, 255, 0.3),
                inset 0 -1px 2px rgba(0, 0, 0, 0.2),
                0 2px 4px rgba(255, 152, 0, 0.2)
              `,
              transition: 'all 0.3s ease',
            }}
          >
            Beta Version
          </Typography>
        </Box>

        {/* Right-aligned items */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            component={Link}
            to="/profile"
            variant={isActiveRoute('/profile') ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              color: isActiveRoute('/profile') ? '#fff' : '#04377b',
              bgcolor: isActiveRoute('/profile') ? '#04377b' : 'transparent',
              '&:hover': {
                bgcolor: isActiveRoute('/profile') ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Profile
          </Button>
          <Button
            variant="text"
            onClick={handleLogout}
            sx={{
              textTransform: 'none',
              color: '#04377b',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(4, 55, 123, 0.05)',
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Sub Navigation Bar and Content Container */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          backgroundImage: 'url(/assets/login-hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          px: { xs: 3, md: 8 },
          py: { xs: 4, md: 6 },
          overflow: 'visible', // Ensure sticky positioning works
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.78) 45%, rgba(255,255,255,0.65) 65%)',
            zIndex: 0,
          },
        }}
      >
        {/* Instrument Sidebar - Fixed position below navigation */}
        <Box
          sx={{
            position: 'fixed',
            top: '69px', // 64px navigation bar + 5px gap
            left: 11, // Move 11px from left edge to show borders (1px to the left)
            zIndex: 1000,
            height: 'calc(100vh - 69px)', // Adjusted for top position
            display: { xs: 'none', md: 'flex' },
            alignItems: 'stretch',
            mr: 2,
          }}
        >
          <InstrumentSidebar onInstrumentSelect={handleInstrumentSelect} />
        </Box>

        {/* Content Area with Sub Navigation */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Sub Navigation Bar - Fixed position below navigation */}
          <Box
            component="div"
            sx={{
              position: 'fixed',
              top: '69px', // Same top position as instrument card (64px + 5px gap)
              left: { xs: 0, md: '368px' }, // Aligned with session card start (362px content area - 40px negative margin)
              right: 0,
              zIndex: 999,
              mt: 0, // No top margin to align with instrument card
              pl: { xs: 3, md: 0 }, // Match header padding on mobile only
              mb: 2,
            }}
          >
            <SubNavigationBar />
          </Box>
          {/* Main Content - Offset for fixed sidebar and sub-nav */}
          <Box sx={{
            width: '100%',
            maxWidth: 1200,
            position: 'relative',
            zIndex: 1,
            color: '#04377b',
            flex: 1,
            mt: { xs: 0, md: '48px' }, // Offset for fixed sub-navigation bar
            ml: { xs: 0, md: '362px' }, // Offset for fixed instrument sidebar
          }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

